import type { TradeData } from '@/lib/types/tradeData';
import type { Order } from '@/lib/services/newstory.service';

import type { CandleSnapshot } from './hyper';
import {
  isApproximatelyEqual,
  roundAmount,
  roundCurrency,
  roundLeverage,
  roundPrice,
} from './number.utils';

/**
 * Filter and merge trades based on price data timestamps
 * @param trades - Array of trade data
 * @param priceData - Array of price data points
 * @returns Filtered and merged trades aligned with price data timestamps
 */
export function filterAndMergeTrades(
  trades: TradeData[],
  priceData: CandleSnapshot,
): TradeData[] {
  if (priceData.length === 0 || trades.length === 0) {
    return [];
  }

  // Get min and max timestamps from price data
  const priceTimestamps = priceData
    .map((point) => point.t / 1000)
    .sort((a, b) => a - b);
  const minTimestamp = priceTimestamps[0];
  const maxTimestamp = priceTimestamps.at(-1)!;

  // Filter trades within price data range
  const filteredTrades = trades.filter(
    (trade) =>
      trade.timestamp >= minTimestamp && trade.timestamp <= maxTimestamp,
  );
  // console.log('trades', trades)
  // console.log('priceTimestamps', priceTimestamps)
  // console.log('minTimestamp', minTimestamp)
  // console.log('maxTimestamp', maxTimestamp)
  // console.log('filteredTrades', filteredTrades)

  // Group trades by their assigned price data timestamp
  const tradeGroups = new Map<number, TradeData[]>();

  filteredTrades.forEach((trade) => {
    // Find the first price data timestamp that is >= trade timestamp
    const assignedTimestamp = priceTimestamps.find(
      (ts) => ts >= trade.timestamp,
    );

    if (assignedTimestamp) {
      if (!tradeGroups.has(assignedTimestamp)) {
        tradeGroups.set(assignedTimestamp, []);
      }
      tradeGroups.get(assignedTimestamp)!.push(trade);
    }
  });

  // Merge trades at the same timestamp by type
  const mergedTrades: TradeData[] = [];

  tradeGroups.forEach((tradesAtTimestamp, timestamp) => {
    // Group by trade type
    const buyTrades = tradesAtTimestamp.filter((trade) => trade.type === 'buy');
    const sellTrades = tradesAtTimestamp.filter(
      (trade) => trade.type === 'sell',
    );

    // Merge buy trades
    if (buyTrades.length > 0) {
      const mergedBuy = mergeSameTypeTrades(buyTrades, 'buy');
      mergedBuy.timestamp = timestamp;
      mergedTrades.push(mergedBuy);
    }

    // Merge sell trades
    if (sellTrades.length > 0) {
      const mergedSell = mergeSameTypeTrades(sellTrades, 'sell');
      mergedSell.timestamp = timestamp;
      mergedTrades.push(mergedSell);
    }
  });

  // Sort by timestamp
  return mergedTrades.sort((a, b) => a.timestamp - b.timestamp);
}

/**
 * Merge trades of the same type at the same timestamp
 * @param trades - Array of trades of the same type
 * @param type - Trade type ('buy' or 'sell')
 * @returns Merged trade data
 */
function mergeSameTypeTrades(
  trades: TradeData[],
  type: 'buy' | 'sell',
): TradeData {
  if (trades.length === 1) {
    return trades[0];
  }

  // Calculate totals
  const totalAmount = trades.reduce((sum, trade) => sum + trade.amount, 0);
  const totalValue = trades.reduce((sum, trade) => sum + trade.total, 0);
  const totalPositionValue = trades.reduce(
    (sum, trade) => sum + trade.positionValue,
    0,
  );
  const totalSize = trades.reduce((sum, trade) => sum + trade.size, 0);

  // Calculate weighted average price
  const weightedPriceSum = trades.reduce(
    (sum, trade) => sum + trade.price * trade.amount,
    0,
  );
  const averagePrice = totalAmount > 0 ? weightedPriceSum / totalAmount : 0;

  // Calculate leverage: total position value / total value
  const averageLeverage = totalValue > 0 ? totalPositionValue / totalValue : 1;

  return {
    timestamp: trades[0].timestamp, // Will be updated by caller
    type,
    price: roundPrice(averagePrice),
    amount: roundAmount(totalAmount),
    total: roundCurrency(totalValue),
    leverage: roundLeverage(averageLeverage),
    size: roundAmount(totalSize),
    positionValue: roundCurrency(totalPositionValue),
  };
}

/**
 * Get trades summary by type
 * @param trades - Array of trade data
 * @returns Summary object with buy and sell totals
 */
export function getTradesSummary(trades: TradeData[]) {
  const buyTrades = trades.filter((trade) => trade.type === 'buy');
  const sellTrades = trades.filter((trade) => trade.type === 'sell');

  const buyTotal = buyTrades.reduce((sum, trade) => sum + trade.total, 0);
  const sellTotal = sellTrades.reduce((sum, trade) => sum + trade.total, 0);
  const buyPositionValue = buyTrades.reduce(
    (sum, trade) => sum + trade.positionValue,
    0,
  );
  const sellPositionValue = sellTrades.reduce(
    (sum, trade) => sum + trade.positionValue,
    0,
  );

  return {
    totalTrades: trades.length,
    buyTrades: {
      count: buyTrades.length,
      total: buyTotal,
      positionValue: buyPositionValue,
      averageLeverage: buyTotal > 0 ? buyPositionValue / buyTotal : 0,
    },
    sellTrades: {
      count: sellTrades.length,
      total: sellTotal,
      positionValue: sellPositionValue,
      averageLeverage: sellTotal > 0 ? sellPositionValue / sellTotal : 0,
    },
    netTotal: buyTotal - sellTotal,
    netPositionValue: buyPositionValue - sellPositionValue,
  };
}

/**
 * Validate trade data
 * @param trade - Trade data to validate
 * @returns True if valid, false otherwise
 */
export function validateTradeData(trade: TradeData): boolean {
  return (
    trade.timestamp > 0 &&
    (trade.type === 'buy' || trade.type === 'sell') &&
    trade.price > 0 &&
    trade.amount > 0 &&
    trade.total > 0 &&
    trade.leverage > 0 &&
    trade.size > 0 &&
    trade.positionValue > 0 &&
    // Validate that positionValue equals total * leverage (with small tolerance for rounding)
    isApproximatelyEqual(
      trade.positionValue,
      trade.total * trade.leverage,
      0.01,
    )
  );
}

/**
 * Get timestamp range from trades
 * @param trades - Array of trade data
 * @returns Object with min and max timestamps
 */
export function getTradeTimestampRange(trades: TradeData[]) {
  if (trades.length === 0) {
    return { min: 0, max: 0 };
  }

  const timestamps = trades.map((trade) => trade.timestamp);
  return {
    min: Math.min(...timestamps),
    max: Math.max(...timestamps),
  };
}
export function getDecimalFormPrice(price: string): number {
  try {
    if (price.includes('.')) {
      const parts = price.split('.');
      if (parts.length > 2) {
        throw new Error('Invalid price format');
      }
      // const integerPart = parts[0]
      const decimalPart = parts[1].replace(/0+$/, ''); // Remove trailing zeros
      return decimalPart.length;
    }
    return 1;
  } catch {
    return 1;
  }
}

/**
 * Convert Order to TradeData format
 * @param order - Order from story service
 * @returns TradeData in standardized format
 */
export function convertTradeOrderToTradeData(order: Order): TradeData {
  const timestamp = order.createdAt;
  const type =
    order.orderType === 'open'
      ? order.side === 'long'
        ? 'buy'
        : 'sell'
      : order.side === 'long'
        ? 'sell'
        : 'buy';

  const total = order.size * order.price;
  const positionValue = order.size * order.price;

  return {
    timestamp,
    type,
    price: roundPrice(order.price),
    amount: roundAmount(order.size),
    total: roundCurrency(total),
    leverage: roundLeverage(order.leverage),
    size: roundAmount(order.size),
    positionValue: roundCurrency(positionValue),
  };
}
